import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Logo } from "../components/Logo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Loader2, Building2, UserRound } from "lucide-react";

const Register = () => {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [role, setRole] = useState("organisation");
  const [form, setForm] = useState({ name: "", email: "", password: "", organisation_name: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        name: form.name,
        email: form.email,
        password: form.password,
        role,
      };
      // Only include organisation_name for organisation role
      if (role === "organisation") {
        payload.organisation_name = form.organisation_name || form.name;
      }
      const user = await register(payload);
      toast.success(`Account created — welcome, ${user.name.split(" ")[0]}!`);
      navigate(role === "organisation" ? "/post-requirement" : "/tell-us");
    } catch (err) {
      // Handle FastAPI 422 validation errors (detail is an array)
      const detail = err?.response?.data?.detail;
      let errorMsg = "Registration failed";
      if (Array.isArray(detail)) {
        errorMsg = detail.map(e => e.msg).join(", ");
      } else if (typeof detail === "string") {
        errorMsg = detail;
      }
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const RoleCard = ({ value, icon: Icon, title, desc }) => {
    const active = role === value;
    return (
      <button
        type="button"
        onClick={() => setRole(value)}
        className={`flex flex-1 flex-col items-start gap-1 rounded-xl border-2 p-4 text-left transition-all ${
          active ? "border-[#1E4FA3] bg-[#1E4FA3]/5" : "border-slate-200 bg-white hover:border-slate-300"
        }`}
      >
        <Icon className={`h-5 w-5 ${active ? "text-[#1E4FA3]" : "text-slate-400"}`} />
        <span className="text-[15px] font-semibold text-[#0B2545]">{title}</span>
        <span className="text-xs text-slate-500">{desc}</span>
      </button>
    );
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-[#f5f9fd] via-[#f7fbf8] to-white px-4 py-10">
      <div className="w-full max-w-md">
        <Link to="/" className="mb-8 flex items-center justify-center">
          <Logo size={54} />
        </Link>

        <div className="rounded-2xl border border-slate-100 bg-white p-7 shadow-sm sm:p-8">
          <h1 className="font-heading text-2xl font-bold text-[#0B2545]">Create your account</h1>
          <p className="mt-1 text-sm text-slate-500">Join the Healthcare Workforce Network.</p>

          <div className="mt-5 flex gap-3">
            <RoleCard value="organisation" icon={Building2} title="Organisation" desc="Hire talent" />
            <RoleCard value="professional" icon={UserRound} title="Professional" desc="Find work" />
          </div>

          <form onSubmit={submit} className="mt-5 space-y-4">
            <div>
              <Label htmlFor="name">{role === "organisation" ? "Contact name" : "Full name"}</Label>
              <Input id="name" required value={form.name} onChange={set("name")} className="mt-1.5 h-11" placeholder="Jane Doe" />
            </div>
            {role === "organisation" && (
              <div>
                <Label htmlFor="org">Organisation name</Label>
                <Input id="org" value={form.organisation_name} onChange={set("organisation_name")} className="mt-1.5 h-11" placeholder="St. Mary's Hospital" />
              </div>
            )}
            <div>
              <Label htmlFor="remail">Email</Label>
              <Input id="remail" type="email" required value={form.email} onChange={set("email")} className="mt-1.5 h-11" placeholder="you@example.com" />
            </div>
            <div>
              <Label htmlFor="rpassword">Password</Label>
              <Input id="rpassword" type="password" required minLength={6} value={form.password} onChange={set("password")} className="mt-1.5 h-11" placeholder="At least 6 characters" />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="h-11 w-full rounded-xl bg-[#16A34A] text-[15px] font-semibold text-white hover:bg-[#138a3f]"
            >
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create Account
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-slate-500">
            Already have an account?{" "}
            <Link to="/login" className="font-semibold text-[#1E4FA3] hover:underline">
              Log in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
